<template>
  <div class="page-header-container" :class="{ 'page-header-container-scroll': scrollContainer }">
    <div class="page-header-top" :class="{ 'page-header-with-contact-banner': showContactBanner }">
      <Banner v-if="!messageHasBeenSeen && siteMessage" :type="'accent'">
        <span class="site-message" v-html="siteMessage"></span>
        <i class="close-site-message af af-close" @click="markMessageAsSeen()" />
      </Banner>

      <PageBanner
        v-if="!siteMessage || showContactBanner"
        :message="bannerMessage"
        :show="showBanner && (showContactBanner || bannerMessage)"
        :show-contact="showContactBanner"
        :about-url="$router.resolve({ name: 'aboutus' }).href"
        :chat-url="$router.resolve({ name: 'contact' }).href"
        :country="country"
        @chat="chat"
        @close="closeBanner"
        @changeCountry="onChangeCountry"
      ></PageBanner>

      <div class="container mx-width">
        <div
          ref="pageHeader"
          class="page-header-primary"
          :class="{ 'page-header-primary-no-menu': !items || !items.length, 'page-header-primary-wrapped': shouldWrapMenu }"
        >
          <div class="page-header-primary-navigation">
            <a v-if="showMobileCategoryMenu" class="page-header-menu-link" @click="openMenu"><span class="af af-menu"></span></a>
            <PageLogo ref="pageLogo" :home-url="$router.resolve({ name: 'home' }).href"></PageLogo>
          </div>
          <div ref="pageMenu" class="page-header-menu-container">
            <LazyHydrate on-interaction>
              <PageMenu :items="items" :route-item="routeItem"></PageMenu>
            </LazyHydrate>
          </div>
          <PageHeaderLinks
            ref="pageLinks"
            :show-cart="showCart"
            :show-contact="!showContactBanner"
            :show-menu-close="showMenu"
            :show-menu-icon="!showMobileCategoryMenu"
            :show-search-icon="!showSearchBar"
            :country="country"
            :search-url="$router.resolve({ name: 'search' }).href"
            :about-url="$router.resolve({ name: 'aboutus' }).href"
            :chat-url="$router.resolve({ name: 'contact' }).href"
            :account-url="$router.resolve({ name: 'menu' }).href"
            :favorites-url="$router.resolve({ name: 'favorites' }).href"
            :cart-url="$router.resolve({ name: 'cart' }).href"
            :cart="cart"
            :cart-count="cartCount"
            :favorites-count="favoritesCount"
            :cart-fetched="cartFetched"
            @chat="chat"
            @checkout="checkout"
            @openMenu="openMenu"
            @closeMenu="closeMenu"
            @toggleSearch="toggleSearch"
            @toggleCart="toggleCart"
            @closeCart="closeCart"
            @changeCountry="onChangeCountry"
          ></PageHeaderLinks>
        </div>

        <div class="page-header-search-container" :class="{ 'page-header-search-container-collapsed': !showSearch }">
          <AlgoliaSearchBar
            ref="search"
            :enable-suggestions="enableSearchSuggestions"
            :initial-query="searchQuery"
            class="search-input"
            @search="onSearch"
            @showSuggestions="showSearchSuggestions"
            @hideSuggestions="hideSearchSuggestions"
          >
          </AlgoliaSearchBar>
        </div>
      </div>
    </div>

    <Lazy v-if="showMobileCategoryMenu">
      <template slot-scope="{}">
        <Drawer :open="showMenu" class="mobile-menu-drawer" :width="null" @hide="closeMenu">
          <PageMenuDrawer
            :items="items"
            :route-item="routeItem"
            :country="country"
            :about-url="$router.resolve({ name: 'aboutus' }).href"
            :favorites-url="$router.resolve({ name: 'favorites' }).href"
            :account-url="$router.resolve({ name: 'orders' }).href"
            :help-center-url="$router.resolve({ name: 'helpCenter' }).href"
            @changeCountry="onChangeCountry"
            @close="closeMenu"
          ></PageMenuDrawer>
        </Drawer>
      </template>
    </Lazy>
    <Lazy v-else>
      <template slot-scope="{}">
        <div v-show="showMenu" class="page-menu-drawer-container">
          <PageMenuDrawerOld
            :items="drawerItems"
            :route-item="routeItem"
            :country="country"
            :about-url="$router.resolve({ name: 'aboutus' }).href"
            :favorites-url="$router.resolve({ name: 'favorites' }).href"
            :chat-url="$router.resolve({ name: 'contact' }).href"
            @chat="chat"
            @changeCountry="onChangeCountry"
          ></PageMenuDrawerOld>
        </div>
      </template>
    </Lazy>

    <BackgroundMask :show="showCart" @click="closeCart"></BackgroundMask>
  </div>
</template>
<script>
import { Drawer, MediaUtils, Lazy } from '@article/pixel';
import LazyHydrate from '@/components/lazy/LazyHydrate.js';
import useResize from '@/compositions/useResize.js';

import Chronicle from '@/components/commons/Chronicle.js';
import LocalStorage, { LocalStorageKeys } from '@/components/commons/LocalStorage.js';
import Banner from './Banner.vue';
import PageBanner from './PageBanner.vue';
import PageMenu from './PageMenu.vue';
import PageLogo from './PageLogo.vue';
import PageHeaderLinks from './PageHeaderLinks.vue';
import BackgroundMask from './BackgroundMask.vue';
import AlgoliaSearchBar from './AlgoliaSearchBar.vue';

export default {
  components: {
    PageMenu,
    PageMenuDrawer: () => import('./PageMenuDrawer.vue'),
    PageMenuDrawerOld: () => import('./PageMenuDrawerOld.vue'),
    PageBanner,
    PageLogo,
    PageHeaderLinks,
    AlgoliaSearchBar,
    BackgroundMask,
    Banner,
    Lazy,
    Drawer,
    LazyHydrate
  },

  props: {
    items: {
      type: Array,
      default: () => [],
    },
    routeItem: {
      type: Object,
    },
    country: {
      type: String,
      default: 'US',
    },
    cart: {
      type: Object,
      default: () => ({
        isEmpty: true,
      }),
    },
    siteMessage: String,
    bannerMessage: String,
    cartCount: Number,
    favoritesCount: Number,
    showContactBanner: Boolean,
    showSearchBar: Boolean,
    showMobileCategoryMenu: Boolean,
    enableSearchSuggestions: Boolean,
    searchQuery: String,
    scrollContainer: Boolean,
    cartFetched: Boolean,
  },

  data() {
    return {
      messageHasBeenSeen: false,
      showSearch: this.showSearchBar,
      showCart: false,
      showBanner: true,
      showMenu: false,
      shouldWrapMenu: false,
    };
  },

  setup() {
    const { width } = useResize();
    return {
      screenWidth: width
    };
  },

  computed: {
    drawerItems() {
      const items = [...this.items];
      items.push({
        id: 'account',
        url: this.$router.resolve({ name: 'menu' }).href,
        name: 'My Account',
        highlight: true,
      });
      items.push({
        id: 'favorites',
        url: this.$router.resolve({ name: 'favorites' }).href,
        name: 'Favorites',
        highlight: true,
      });
      return items;
    },
  },

  watch: {
    siteMessage: {
      handler() {
        this.checkIfMessageSeen();
      },
      immediate: true,
    },
    screenWidth: {
      handler(newValue, prevValue) {
        this.onResize();
      }
    }
  },

  mounted() {
    this.toggleMiniCartTimeOut = null;

    if (this.showSearchBar && this.$refs.search && !MediaUtils.isTabletAndSmaller) {
      this.$refs.search.focus();
    }
  },

  methods: {
    chat() {
      this.$emit('chat');
    },

    checkIfMessageSeen() {
      let seenMessages = [];
      try {
        seenMessages = JSON.parse(LocalStorage.get(LocalStorageKeys.SITE_WIDE_MESSAGES) || '[]');
      } catch (e) {}

      if (!this.siteMessage) {
        return false;
      }

      if (!Array.isArray(seenMessages)) {
        seenMessages = [];
      }

      const currentMessage = this.siteMessage.replace(/\s/g, '');

      this.messageHasBeenSeen = seenMessages.indexOf(currentMessage) !== -1;
    },

    markMessageAsSeen() {
      this.messageHasBeenSeen = true;

      const savedMessage = this.siteMessage.replace(/\s/g, '');

      let currentSeen = [];
      try {
        currentSeen = JSON.parse(LocalStorage.get(LocalStorageKeys.SITE_WIDE_MESSAGES) || '[]');
      } catch (e) {}

      if (!Array.isArray(currentSeen)) {
        currentSeen = [];
      }

      currentSeen.push(savedMessage);

      LocalStorage.set(LocalStorageKeys.SITE_WIDE_MESSAGES, JSON.stringify(currentSeen));
    },

    onSearch(e) {
      this.$emit('search', e);
    },

    checkout() {
      this.$emit('checkout');
    },

    onChangeCountry(country) {
      this.$emit('changeCountry', country);
    },

    toggleSearch() {
      this.showSearch = !this.showSearch;
      this.showCart = false;

      this.$nextTick(() => {
        if (this.showSearch && this.$refs.search) {
          this.$refs.search.focus();
        }
      });
    },

    toggleCart() {
      this.showCart = !this.showCart;

      if (this.showCart) {
        this.$emit('openCart');
      }
    },

    closeCart() {
      this.showCart = false;
    },

    openMenu() {
      this.showMenu = true;
      this.showCart = false;

      if (!this.showMobileCategoryMenu) {
        this.$emit('openMenu');
      }
    },

    closeMenu() {
      this.showMenu = false;
      this.showCart = false;

      this.$emit('closeMenu');
    },

    showSearchSuggestions() {
      this.showMenu = false;
      this.showCart = false;

      this.$emit('openMenu');
    },

    hideSearchSuggestions() {
      if (!this.showMobileCategoryMenu) {
        this.showMenu = false;
      }
      this.showCart = false;

      this.$emit('closeMenu');
    },

    closeBanner() {
      this.showBanner = false;

      this.$emit('closeBanner');
    },

    notifyCartItemsAdded() {
      if (this.toggleMiniCartTimeOut) {
        clearTimeout(this.toggleMiniCartTimeOut);
        this.toggleMiniCartTimeOut = null;
      }

      this.showCart = true;

      const useNewMiniCart = Chronicle.getFeatureStatus('fly-out-enabled-2') === 'enabled';

      if(useNewMiniCart) {
        return false;
      }

      this.toggleMiniCartTimeOut = setTimeout(() => {
        this.showCart = false;
      }, 5000);
    },

    onResize() {
      const pageHeaderWidth = this.$refs.pageHeader.getBoundingClientRect().width;
      const pageLogoWidth = this.$refs.pageLogo.$el.getBoundingClientRect().width;
      const pageMenuWidth = this.$refs.pageMenu.getBoundingClientRect().width;
      const pageLinksWidth = this.$refs.pageLinks.$el.getBoundingClientRect().width;

      this.shouldWrapMenu = pageLogoWidth + pageMenuWidth + pageLinksWidth >= pageHeaderWidth;
    },
  },
};
</script>
<style lang="scss">
@import '~@article/pixel/dist/style/variables';

.page-header-container {
  .mobile-menu-drawer {
    .navigation-drawer-content {
      max-width: 280px;
    }
  }
  .site-message {
    display: inline-block;
    flex-grow: 1;
    text-align: left;
    line-height: var(--line-height-16);
    font-size: var(--font-size-16);
    width: calc(100% - 30px);
  }
  .close-site-message {
    display: inline-block;
    width: 25px;
    right: -5px;
    text-align: right;
    color: var(--accent-color);
    cursor: pointer;
    font-size: 12px;
  }

  &.page-header-container-scroll {
    height: 100%;
    overflow-y: scroll;
  }

  .page-header-top {
    position: relative;
    z-index: 12;
    background-color: var(--white);
  }

  .page-header-primary {
    position: relative;
    padding-top: 15px;
    padding-bottom: 15px;
    margin-top: 0px;
    margin-bottom: 0px;
    display: flex;
    align-items: center;
  }

  .page-header-primary-navigation {
    display: flex;
    align-items: center;
  }

  .page-header-menu-link {
    padding-right: 16px;

    @media (min-width: $tablet-breakpoint) {
      display: none;
    }
  }

  .page-header-primary-wrapped {
    flex-wrap: wrap;

    .page-header-menu-container {
      order: 1;
    }
  }

  .page-header-menu-container {
    display: none;
  }

  .page-header-search-container {
    height: auto;
    opacity: 1;

    &.page-header-search-container-collapsed {
      display: none;
      opacity: 0;
    }
  }

  .search-input {
    margin-top: 0px;
    margin-bottom: var(--space-3);
  }

  .page-menu-drawer-container {
    display: block;
  }

  @media (min-width: $tablet-breakpoint) {
    .page-header-primary {
      align-items: baseline;
      padding-top: 0px;
      padding-bottom: 0px;
      margin-top: 15px;
      margin-bottom: 0px;
    }

    .page-header-menu-container {
      display: block;
    }

    .page-menu-drawer-container {
      display: none;
    }
  }

  @media (min-width: $mobile-breakpoint) {
    .background-mask-container {
      display: none;
    }
  }

  @media (min-width: $large-breakpoint) {
    .page-header-primary.page-header-primary-no-menu {
      margin-bottom: 15px;
    }
  }

  .page-header-with-contact-banner {
    @media (min-width: $large-breakpoint) {
      .search-input {
        margin-top: 0px;
        margin-bottom: 10px;
      }
    }
  }
}
</style>
